import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Bot,
  CalendarClock,
  Megaphone,
  PlusCircle,
  Rocket,
  Sparkles
} from 'lucide-react';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { AppContext } from '../context/AppContext';
import { useCoFounder } from '../context/CoFounderContext';

const defaultCampaignDraft = {
  name: '',
  objective: '',
  channel: 'email',
  audience: '',
  budget: ''
};

const defaultAutomationDraft = {
  name: '',
  trigger: 'weekly',
  cadenceDays: 7,
  enabled: true
};

const getNextRunDate = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + Number(days || 7));
  return date;
};

const MarketingPage = () => {
  const { currentUser, isDemoMode } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  const { sendMessage, setIsOpen, setPageContext } = useCoFounder();

  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [settings, setSettings] = useState({
    autopilotEnabled: false,
    tone: 'balanced'
  });
  const [campaignDraft, setCampaignDraft] = useState(defaultCampaignDraft);
  const [automationDraft, setAutomationDraft] = useState(defaultAutomationDraft);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    setPageContext('Marketing module - campaigns, automations, and performance.');
  }, [setPageContext]);

  useEffect(() => {
    const loadMarketingData = async () => {
      if (!currentUser) {
        setCampaigns([]);
        setAutomations([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setStatusMessage('');
      try {
        if (isDemoMode) {
          setCampaigns([
            {
              id: 'demo-campaign-1',
              name: 'Back To School Drive',
              objective: 'Lead generation',
              channel: 'email',
              status: 'active',
              budget: 5000,
              createdAt: new Date()
            }
          ]);
          setAutomations([
            {
              id: 'demo-auto-1',
              name: 'Weekly Offer Reminder',
              trigger: 'weekly',
              cadenceDays: 7,
              enabled: true,
              nextRun: getNextRunDate(2)
            }
          ]);
          setSettings({ autopilotEnabled: true, tone: 'assertive' });
          return;
        }

        const marketingQuery = query(
          collection(db, 'marketing'),
          where('createdBy', '==', currentUser.uid)
        );
        const snapshot = await getDocs(marketingQuery);
        let records = snapshot.docs.map((recordDoc) => ({ id: recordDoc.id, ...recordDoc.data() }));
        if (activeBusiness?.id) {
          records = records.filter((record) => !record.businessId || record.businessId === activeBusiness.id);
        }

        const campaignRecords = records
          .filter((record) => (record.recordType || 'campaign') === 'campaign')
          .map((record) => ({
            ...record,
            createdAt: record.createdAt?.toDate ? record.createdAt.toDate() : new Date()
          }))
          .sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));

        const automationRecords = records
          .filter((record) => record.recordType === 'automation')
          .map((record) => ({
            ...record,
            nextRun: record.nextRun?.toDate ? record.nextRun.toDate() : null,
            lastRun: record.lastRun?.toDate ? record.lastRun.toDate() : null
          }))
          .sort((a, b) => (a.nextRun?.getTime?.() || 0) - (b.nextRun?.getTime?.() || 0));

        const settingsRecord = records.find((record) => record.recordType === 'settings');
        if (settingsRecord) {
          setSettings({
            autopilotEnabled: Boolean(settingsRecord.autopilotEnabled),
            tone: settingsRecord.tone || 'balanced'
          });
        }

        setCampaigns(campaignRecords);
        setAutomations(automationRecords);
      } catch (error) {
        console.error('Error loading marketing module:', error);
        setStatusMessage('Could not load marketing data.');
      } finally {
        setLoading(false);
      }
    };

    loadMarketingData();
  }, [currentUser, isDemoMode, activeBusiness?.id]);

  const dueAutomations = useMemo(() => {
    const now = new Date();
    return automations.filter((automation) =>
      automation.enabled && automation.nextRun && automation.nextRun <= now
    );
  }, [automations]);

  const createCampaign = async () => {
    if (!campaignDraft.name.trim()) return;

    try {
      if (isDemoMode) {
        setCampaigns((prev) => [{
          id: `demo-campaign-${Date.now()}`,
          ...campaignDraft,
          recordType: 'campaign',
          status: 'draft',
          deliberateBy: 'cofounder',
          budget: Number(campaignDraft.budget || 0),
          createdAt: new Date()
        }, ...prev]);
        setCampaignDraft(defaultCampaignDraft);
        return;
      }

      const payload = {
        ...campaignDraft,
        budget: Number(campaignDraft.budget || 0),
        recordType: 'campaign',
        status: 'draft',
        deliberateBy: 'cofounder',
        businessId: activeBusiness?.id || null,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      await addDoc(collection(db, 'marketing'), payload);
      setCampaignDraft(defaultCampaignDraft);
      setStatusMessage('Campaign created. Ask coFounder to generate copy and launch steps.');
      setCampaigns((prev) => [{
        ...payload,
        id: `local-${Date.now()}`,
        createdAt: new Date()
      }, ...prev]);
    } catch (error) {
      console.error('Error creating campaign:', error);
      setStatusMessage('Campaign creation failed.');
    }
  };

  const createAutomation = async () => {
    if (!automationDraft.name.trim()) return;

    const nextRun = getNextRunDate(automationDraft.cadenceDays);

    try {
      if (isDemoMode) {
        setAutomations((prev) => [{
          id: `demo-auto-${Date.now()}`,
          ...automationDraft,
          cadenceDays: Number(automationDraft.cadenceDays || 7),
          nextRun
        }, ...prev]);
        setAutomationDraft(defaultAutomationDraft);
        return;
      }

      if (!currentUser?.uid) {
        setStatusMessage('Please sign in to create automations.');
        return;
      }

      const payload = {
        ...automationDraft,
        cadenceDays: Number(automationDraft.cadenceDays || 7),
        recordType: 'automation',
        businessId: activeBusiness?.id || null,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        nextRun
      };

      await addDoc(collection(db, 'marketing'), payload);
      setStatusMessage('Automation added. It can now be run deliberately with coFounder.');
      setAutomationDraft(defaultAutomationDraft);
      setAutomations((prev) => [{
        id: `local-${Date.now()}`,
        ...automationDraft,
        cadenceDays: Number(automationDraft.cadenceDays || 7),
        nextRun
      }, ...prev]);
    } catch (error) {
      console.error('Error creating automation:', error);
      setStatusMessage('Automation creation failed.');
    }
  };

  const saveSettings = async (nextSettings) => {
    setSettings(nextSettings);
    if (isDemoMode || !currentUser) return;

    const settingsDocId = `settings_${activeBusiness?.id || currentUser.uid}`;
    await setDoc(doc(db, 'marketing', settingsDocId), {
      recordType: 'settings',
      businessId: activeBusiness?.id || null,
      createdBy: currentUser.uid,
      ...nextSettings,
      updatedAt: serverTimestamp()
    }, { merge: true });
  };

  const askCofounderToPlan = async () => {
    setIsOpen(true);
    await sendMessage(
      `Build a deliberate marketing plan for my business for the next 14 days. Focus on high-intent channels, campaign sequencing, and measurable outcomes. Business context: ${activeBusiness?.name || 'active business'}.`
    );
  };

  const runDueAutomations = async () => {
    if (dueAutomations.length === 0) {
      setStatusMessage('No automations are currently due.');
      return;
    }

    const summary = dueAutomations
      .map((automation) => `- ${automation.name} (${automation.trigger}, every ${automation.cadenceDays} days)`)
      .join('\n');

    setIsOpen(true);
    await sendMessage(
      `Run these deliberate marketing automations and propose the exact copy/content before execution:\n${summary}\nFor each automation, include objective, channel, audience, and CTA.`
    );

    if (!isDemoMode) {
      await Promise.all(
        dueAutomations
          .filter((automation) => automation.id && !automation.id.startsWith('local-'))
          .map((automation) => updateDoc(doc(db, 'marketing', automation.id), {
            lastRun: serverTimestamp(),
            nextRun: getNextRunDate(automation.cadenceDays || 7),
            updatedAt: serverTimestamp()
          }))
      );
    }

    setAutomations((prev) =>
      prev.map((automation) =>
        dueAutomations.some((due) => due.id === automation.id)
          ? { ...automation, lastRun: new Date(), nextRun: getNextRunDate(automation.cadenceDays || 7) }
          : automation
      )
    );
    setStatusMessage(`Sent ${dueAutomations.length} automation tasks to coFounder for deliberate execution.`);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Marketing Module</h1>
            <p className="text-sm text-gray-500 mt-1">
              Deliberate auto-marketing workflows powered by coFounder.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={askCofounderToPlan}
              className="px-4 py-2 rounded-lg bg-[#190192] text-white hover:bg-[#2C0DB5] inline-flex items-center text-sm"
            >
              <Sparkles size={16} className="mr-2" />
              Ask coFounder for plan
            </button>
            <button
              onClick={runDueAutomations}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 inline-flex items-center text-sm"
            >
              <Rocket size={16} className="mr-2" />
              Run due automations
            </button>
          </div>
        </div>
      </div>

      {statusMessage && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
          {statusMessage}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <p className="text-xs uppercase text-gray-500">Campaigns</p>
          <p className="text-2xl font-semibold text-gray-900 mt-1">{campaigns.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <p className="text-xs uppercase text-gray-500">Automations</p>
          <p className="text-2xl font-semibold text-gray-900 mt-1">{automations.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <p className="text-xs uppercase text-gray-500">Due now</p>
          <p className="text-2xl font-semibold text-amber-700 mt-1">{dueAutomations.length}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
          <h2 className="font-semibold text-gray-900 inline-flex items-center">
            <Megaphone size={16} className="mr-2 text-[#190192]" />
            New Campaign
          </h2>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Campaign name"
            value={campaignDraft.name}
            onChange={(event) => setCampaignDraft((prev) => ({ ...prev, name: event.target.value }))}
          />
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Objective"
            value={campaignDraft.objective}
            onChange={(event) => setCampaignDraft((prev) => ({ ...prev, objective: event.target.value }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={campaignDraft.channel}
              onChange={(event) => setCampaignDraft((prev) => ({ ...prev, channel: event.target.value }))}
            >
              <option value="email">Email</option>
              <option value="social">Social</option>
              <option value="sms">SMS</option>
              <option value="ads">Ads</option>
            </select>
            <input
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Budget"
              value={campaignDraft.budget}
              onChange={(event) => setCampaignDraft((prev) => ({ ...prev, budget: event.target.value }))}
            />
          </div>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Audience segment"
            value={campaignDraft.audience}
            onChange={(event) => setCampaignDraft((prev) => ({ ...prev, audience: event.target.value }))}
          />
          <button
            onClick={createCampaign}
            className="px-4 py-2 rounded-lg bg-[#190192] text-white text-sm inline-flex items-center hover:bg-[#2C0DB5]"
          >
            <PlusCircle size={16} className="mr-2" />
            Create campaign
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
          <h2 className="font-semibold text-gray-900 inline-flex items-center">
            <Bot size={16} className="mr-2 text-[#190192]" />
            Automation Engine
          </h2>
          <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
            <span className="text-sm text-gray-700">Autopilot</span>
            <button
              onClick={() => saveSettings({ ...settings, autopilotEnabled: !settings.autopilotEnabled })}
              className={`w-11 h-6 rounded-full transition-colors ${settings.autopilotEnabled ? 'bg-[#190192]' : 'bg-gray-300'}`}
              aria-label="Toggle autopilot"
            >
              <span className={`block w-5 h-5 bg-white rounded-full mt-0.5 transition-transform ${settings.autopilotEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={settings.tone}
            onChange={(event) => saveSettings({ ...settings, tone: event.target.value })}
          >
            <option value="balanced">Balanced tone</option>
            <option value="assertive">Assertive tone</option>
            <option value="conversational">Conversational tone</option>
            <option value="educational">Educational tone</option>
          </select>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Automation name"
            value={automationDraft.name}
            onChange={(event) => setAutomationDraft((prev) => ({ ...prev, name: event.target.value }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={automationDraft.trigger}
              onChange={(event) => setAutomationDraft((prev) => ({ ...prev, trigger: event.target.value }))}
            >
              <option value="weekly">Weekly</option>
              <option value="new_lead">New lead</option>
              <option value="low_engagement">Low engagement</option>
              <option value="event_based">Event-based</option>
            </select>
            <input
              type="number"
              min="1"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={automationDraft.cadenceDays}
              onChange={(event) => setAutomationDraft((prev) => ({ ...prev, cadenceDays: Number(event.target.value || 7) }))}
            />
          </div>
          <button
            onClick={createAutomation}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm inline-flex items-center hover:bg-gray-50"
          >
            <CalendarClock size={16} className="mr-2" />
            Add automation
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Campaign Pipeline</h3>
          {loading ? (
            <p className="text-sm text-gray-500">Loading campaigns...</p>
          ) : campaigns.length === 0 ? (
            <p className="text-sm text-gray-500">No campaigns yet.</p>
          ) : (
            <div className="space-y-3">
              {campaigns.slice(0, 8).map((campaign) => (
                <div key={campaign.id} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">{campaign.name}</p>
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                      {campaign.status || 'draft'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {campaign.channel || 'channel'} | {campaign.objective || 'objective not set'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Automation Queue</h3>
          {loading ? (
            <p className="text-sm text-gray-500">Loading automations...</p>
          ) : automations.length === 0 ? (
            <p className="text-sm text-gray-500">No automations configured.</p>
          ) : (
            <div className="space-y-3">
              {automations.slice(0, 8).map((automation) => (
                <div key={automation.id} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">{automation.name}</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${automation.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                      {automation.enabled ? 'enabled' : 'disabled'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Trigger: {automation.trigger || 'n/a'} | Next run:{' '}
                    {automation.nextRun ? automation.nextRun.toLocaleDateString() : 'not scheduled'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketingPage;
